import { useEffect, useRef, useState } from "react";
import { api } from "../../api/client";
import { Icon, cx } from "../../components/ui";
import KartuAspek from "./KartuAspek";
import { STATUS_BULAN } from "./DaftarMurid";
import {
  ALASAN_TERKUNCI, NAMA_RUBRIK, SATU_NILAI, hitungBulan, komentarAkhir, namaBulan, ringkasMurid
} from "./logika";

// Isi raport satu murid — layar utama, dirancang untuk HP (§7.3 no. 3).
//
// SIMPAN OTOMATIS per ketukan. Layar langsung berubah (nilai bulan & status dihitung di
// sini), lalu permintaannya dikirim BERURUTAN lewat antrean. Jawaban server — yang
// otoritatif — baru dipasang ketika antrean kosong: kalau dipasang di tengah antrean, ketukan
// yang belum terkirim akan tampak "melompat balik" sesaat. Gagal menyimpan → isian kelas
// dimuat ulang dari server, supaya layar tak pernah menampilkan sesuatu yang tak tersimpan.

const pad2 = (n) => String(n).padStart(2, "0");

// Antrean simpan berjalan satu per satu, jadi satu permintaan yang MENGGANTUNG (sinyal HP
// hilang) akan menahan semua ketukan sesudahnya. Lewat batas ini dianggap gagal → dimuat ulang.
export const BATAS_SIMPAN_MS = 20000;
const denganBatas = (janji) => new Promise((ok, tolak) => {
  const t = setTimeout(() => tolak(new Error("Server tidak menjawab. Periksa koneksi.")), BATAS_SIMPAN_MS);
  janji.then((v) => { clearTimeout(t); ok(v); }, (e) => { clearTimeout(t); tolak(e); });
});

// Bulan yang dibuka pertama: bulan term yang sedang berjalan, kalau ada.
function bulanAwal(term) {
  const kini = new Date();
  const iso = `${kini.getFullYear()}-${pad2(kini.getMonth() + 1)}`;
  const i = term.bulan.indexOf(iso);
  if (i >= 0) return i + 1;
  return iso > term.bulan[2] ? 3 : 1;
}

function TandaSimpan({ simpan, onMuatUlang }) {
  if (!simpan.status) return null;
  if (simpan.status === "menyimpan") {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-500" aria-live="polite">
        <Icon name="progress_activity" className="animate-spin text-sm" />
        Menyimpan…
      </span>
    );
  }
  if (simpan.status === "tersimpan") {
    return (
      <span key={simpan.n} className="anim-pop-sm inline-flex items-center gap-1 text-xs font-bold text-green-600" aria-live="polite">
        <Icon name="check" className="text-sm" />
        Tersimpan
      </span>
    );
  }
  return (
    <span role="alert" className="inline-flex flex-wrap items-center gap-2 text-xs font-bold text-red-600">
      Gagal menyimpan{simpan.pesan ? `: ${simpan.pesan}` : ""}
      <button type="button" onClick={onMuatUlang} className="rounded-md border border-red-300 px-2 py-0.5 hover:bg-red-50 dark:hover:bg-red-500/10">
        Muat ulang
      </button>
    </span>
  );
}

export default function IsiMurid({ bank, rincian, studentId, onPerbarui, onMuatUlang, onPindah, onKembali, onPratinjau }) {
  const { kelas, term, aspek: aspekList, murid: semua } = rincian;
  // Kelompok aspek di layar: Speaking lalu Learning Record (raport SD/SMP/SMA memuat keduanya).
  const kelompok = rincian.bagian && rincian.bagian.length
    ? rincian.bagian
    : [{ kode: kelas.rubrik, label: NAMA_RUBRIK[kelas.rubrik], aspek: aspekList }];
  const murid = semua.find((m) => m.studentId === studentId);
  const [bulanKe, setBulanKe] = useState(() => bulanAwal(term));
  const [simpan, setSimpan] = useState({ status: "", pesan: "", n: 0 });
  const muridRef = useRef(murid);
  const antrian = useRef(Promise.resolve());
  const tertunda = useRef(0);
  useEffect(() => { muridRef.current = murid; });

  const kembali = (
    <button
      type="button"
      onClick={onKembali}
      className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-bold text-slate-500 hover:bg-slate-100 hover:text-primary dark:hover:bg-slate-800"
    >
      <Icon name="chevron_left" />
      {kelas.nama}
    </button>
  );

  if (!murid) {
    return (
      <div className="space-y-4">
        {kembali}
        <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500 dark:border-slate-700">
          Murid ini tidak ada di kelas {kelas.nama}.
        </p>
      </div>
    );
  }

  const bolehUbah = term.bolehTulis && murid.bisaDiisi;
  const bisa = semua.filter((m) => m.bisaDiisi);
  const posisi = bisa.findIndex((m) => m.studentId === studentId);
  const bulan = murid.bulan[bulanKe - 1];
  const labelAspek = Object.fromEntries(((bank.rubrik[kelas.rubrik] || {}).aspek || []).map((a) => [a.kode, a.label]));

  function kirim(panggil) {
    tertunda.current += 1;
    setSimpan((s) => ({ status: "menyimpan", pesan: "", n: s.n }));
    antrian.current = antrian.current.then(async () => {
      let selesai = false;
      try {
        const r = await denganBatas(Promise.resolve(panggil()));
        tertunda.current -= 1;
        selesai = true;
        if (!r || !r.success) throw new Error((r && r.message) || "Gagal menyimpan.");
        if (tertunda.current === 0) {
          const lokal = muridRef.current;
          onPerbarui({
            ...r.murid,
            bulan: r.murid.bulan.map((b, i) => ({ ...b, acuan: lokal && lokal.bulan[i] ? lokal.bulan[i].acuan : null }))
          });
          setSimpan((s) => ({ status: "tersimpan", pesan: "", n: s.n + 1 }));
        }
      } catch (err) {
        if (!selesai) tertunda.current -= 1;
        setSimpan((s) => ({ status: "gagal", pesan: err.message || "", n: s.n }));
        onMuatUlang();
      }
    });
  }

  // Ubah satu aspek di layar, lalu hitung ulang bulan itu & ringkasan muridnya.
  function ubahAspek(ke, aspek, ubah) {
    const m = muridRef.current;
    const baru = m.bulan.map((b) => (b.ke !== ke ? b : hitungBulan(bank, kelas.rubrik, aspekList, {
      ...b, aspek: { ...b.aspek, [aspek]: ubah(b.aspek[aspek]) }
    })));
    const hasil = { ...m, bulan: baru, ...ringkasMurid(baru) };
    muridRef.current = hasil;
    onPerbarui(hasil);
  }

  const simpanNilai = (aspek, kolom, nilai) => {
    ubahAspek(bulanKe, aspek, (x) => {
      const isian = [...(x.isian || [])];
      isian[kolom - 1] = nilai || "";
      return { ...x, isian };
    });
    kirim(() => api.simpanNilaiRaport({
      term: term.id, classId: kelas.id, studentId, isian: [{ bulan: bulanKe, aspek, kolom, nilai }]
    }));
  };

  const simpanKomentar = (aspek, pilihan) => {
    ubahAspek(bulanKe, aspek, (x) => ({ ...x, komentar: { pilihan, nilai: x.nilai, versi: bank.versi } }));
    kirim(() => api.simpanKomentarRaport({
      term: term.id, classId: kelas.id, studentId, bulan: bulanKe, aspek, pilihan
    }));
  };


  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {kembali}
        <TandaSimpan simpan={simpan} onMuatUlang={onMuatUlang} />
      </div>

      <div className="anim-rise flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-black tracking-tight md:text-3xl">{murid.nama}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {murid.studentId} · {NAMA_RUBRIK[kelas.rubrik]} · {term.name}
          </p>
        </div>
        {posisi >= 0 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Murid sebelumnya"
              disabled={posisi <= 0}
              onClick={() => onPindah(bisa[posisi - 1].studentId)}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
            >
              <Icon name="chevron_left" />
            </button>
            <span className="px-1 text-xs font-bold tabular-nums text-slate-500">{posisi + 1} dari {bisa.length}</span>
            <button
              type="button"
              aria-label="Murid berikutnya"
              disabled={posisi >= bisa.length - 1}
              onClick={() => onPindah(bisa[posisi + 1].studentId)}
              className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300"
            >
              <Icon name="chevron_right" />
            </button>
          </div>
        )}
      </div>

      {!bolehUbah && (
        <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300">
          <Icon name="lock" className="text-base" />
          {!term.bolehTulis
            ? "Term ini sudah terkunci — isian hanya bisa dilihat. Minta team leader membuka kuncinya bila perlu memperbaiki."
            : murid.alasan === "kelas-utama-lain" && murid.kelasUtama
            ? `Raport murid ini diisi di kelas ${murid.kelasUtama.nama}.`
            : ALASAN_TERKUNCI[murid.alasan] || "Raport murid ini belum bisa diisi."}
        </p>
      )}

      <div className="anim-rise anim-d1 mt-5 grid grid-cols-3 gap-2" role="tablist" aria-label="Bulan">
        {murid.bulan.map((b, i) => {
          const aktif = b.ke === bulanKe;
          return (
            <button
              key={b.ke}
              type="button"
              role="tab"
              aria-selected={aktif}
              onClick={() => setBulanKe(b.ke)}
              className={cx(
                "lift-press flex flex-col items-center rounded-xl border px-2 py-2 text-center",
                aktif
                  ? "border-primary bg-primary text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-primary/60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
              )}
            >
              <span className="text-sm font-black">Bulan {b.ke}</span>
              <span className={cx("text-[11px]", aktif ? "text-white/80" : "text-slate-400")}>{namaBulan(term.bulan[i])}</span>
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-bold">
                <span className={cx("inline-block h-2.5 w-2.5 rounded-full border-2", STATUS_BULAN[b.status].titik, aktif && b.status === "kosong" && "border-white/70")} />
                {STATUS_BULAN[b.status].label}
              </span>
            </button>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        {(bank.skala || []).map((s) => `${s.kode} = ${s.label}`).join(" · ")}
      </p>

      <div key={bulanKe}>
        {kelompok.map((g, gi) => {
          const akhir = komentarAkhir(bank, kelas.rubrik, g.aspek, bulan, murid.nama);
          const terisi = g.aspek.filter((a) => bulan.aspek[a] && bulan.aspek[a].nilai && bulan.aspek[a].komentar.sah).length;
          return (
            <section key={g.kode} className="mt-6 first:mt-4" aria-label={g.label}>
              {kelompok.length > 1 && (
                <h2 className="mb-3 flex items-baseline justify-between gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
                  <span className="text-lg font-black tracking-tight">{g.label}</span>
                  <span className="text-xs font-bold tabular-nums text-slate-500">{terisi}/{g.aspek.length} aspek lengkap</span>
                </h2>
              )}
              <div className="space-y-4">
                {g.aspek.map((a, i) => (
                  <KartuAspek
                    key={a}
                    bank={bank}
                    rubrik={kelas.rubrik}
                    aspek={a}
                    label={labelAspek[a] || a}
                    satuNilai={SATU_NILAI.has(a)}
                    data={bulan.aspek[a]}
                    acuan={bulan.acuan}
                    bolehUbah={bolehUbah}
                    urutan={gi * 4 + i}
                    onNilai={(kolom, nilai) => simpanNilai(a, kolom, nilai)}
                    onKomentar={(pilihan) => simpanKomentar(a, pilihan)}
                  />
                ))}
              </div>
              <div className="anim-rise mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                <h3 className="text-sm font-black uppercase tracking-wide text-slate-500">
                  Komentar akhir {kelompok.length > 1 ? `${g.label} — ` : ""}Bulan {bulanKe} ({namaBulan(term.bulan[bulanKe - 1])})
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
                  {akhir || "Belum ada kalimat yang dipilih untuk bulan ini."}
                </p>
              </div>
            </section>
          );
        })}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        {murid.bisaDiisi && (
          <button
            type="button"
            // Tunggu antrean simpan kosong: pratinjau disusun server dari isian yang tersimpan.
            onClick={() => antrian.current.then(onPratinjau)}
            className="lift-press inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 hover:border-primary hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <Icon name="description" />
            Pratinjau & terbitkan
          </button>
        )}
        {posisi >= 0 && posisi < bisa.length - 1 && (
          <button
            type="button"
            onClick={() => onPindah(bisa[posisi + 1].studentId)}
            className="lift-press ml-auto inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-white"
          >
            Murid berikutnya: {bisa[posisi + 1].nama}
            <Icon name="arrow_forward" />
          </button>
        )}
      </div>
    </div>
  );
}
